import _ from 'lodash';

import applyDefaultConfiguration from 'applyDefaultConfiguration';
import configureApp from 'configuration/configureApp';
import configureDemo from 'configuration/configureDemo';
import createEnum from 'lib/createEnum';
import { getStage } from 'app/modules';
import { STAGES } from 'app/modules/ui/constants';
import { showWidget, unminimizeWidget, unsuppress } from 'app/modules/ui/actions';
import startApp from 'configuration/startApp';

/**
 * @namespace GladlyHelpApp
 * @description
 *
 * ### JavaScript snippet
 * Add the following JavaScript embed code to all your site’s pages, preferably at the bottom of the page to avoid
 * slowing down the main page content to load. This will load the code that powers the Gladly Help App on your site.
 *
 * ```
 * <script>
 *   (function(c,n,p,t){var i;var o=[];var l=t?t:"https://cdn.gladly.com/chat-sdk/widget.js";c[p]={init:function(){i=arguments;var e={then:function(t){o.push({type:"t",next:t});return e},catch:function(t){o.push({type:"c",next:t});return e}};return e}};function e(){try{var t=n.getElementsByTagName("script")[0];var e=n.createElement("script");e.async=true;e.src=l+"?q="+(new Date).getTime();t.parentNode.insertBefore(e,t)}catch(t){}}c.__onHelpAppHostReady__=function t(e){delete c.__onHelpAppHostReady__;c[p]=e;e.loaderCdn=l;if(i){var n=e.init.apply(e,i);for(var r=0;r<o.length;r++){var a=o[r];if(a.type==="t"){n=n.then(a.next)}else{n=n.catch(a.next)}}}};e()})
 *   (window,document,'GladlyHelpApp')
 * </script>
 * ```
 *
 * If you use a QA environment to verify new releases, the embed code for that site will be slightly different:
 * ```
 * <script>
 *   (function(c,n,p,t){var i;var o=[];var l=t?t:"https://cdn.gladly.com/chat-sdk/widget.js";c[p]={init:function(){i=arguments;var e={then:function(t){o.push({type:"t",next:t});return e},catch:function(t){o.push({type:"c",next:t});return e}};return e}};function e(){try{var t=n.getElementsByTagName("script")[0];var e=n.createElement("script");e.async=true;e.src=l+"?q="+(new Date).getTime();t.parentNode.insertBefore(e,t)}catch(t){}}c.__onHelpAppHostReady__=function t(e){delete c.__onHelpAppHostReady__;c[p]=e;e.loaderCdn=l;if(i){var n=e.init.apply(e,i);for(var r=0;r<o.length;r++){var a=o[r];if(a.type==="t"){n=n.then(a.next)}else{n=n.catch(a.next)}}}};e()})
 *   (window,document,'GladlyHelpApp', 'https://cdn.gladly.qa/gladly/chat-sdk/widget.js')
 * </script>
 * ```
 *
 * ### Initialize
 * In order to start the Gladly Help App on your site, you must initialize it after you load the above
 * JavaScript embed code.
 *
 * #### Basic Initialization
 * Add the following script tag to your page to automatically initialize the Gladly Help App. This is
 * most appropriate if you do not want to take advantage of any Gladly API functionality and just want
 * to display a basic widget. Work with your Gladly CSM to get the “your-own-id” value.
 * ```
 * <script>
 *  window.gladlyConfig = {
 *    appId: 'your-own-id'
 *  };
 * </script>
 * ```
 *
 * #### Manual Initialization
 * GladlyHelpApp can be manually initialized to get better control over when to display the widget.
 * This could be desirable, for example, if you want to start the GladlyHelpApp from your own button.
 *
 * For a more dynamic interaction, GladlyHelpApp exposes an Availability state that allows
 * the site to change the behavior depending on if chat is closed or if it's unavailable due to high
 * demand.
 *
 * The example below initializes the GladlyHelpApp, then checks to see if chat is available and sets
 * a class on a button assumed to exist on the page.
 *
 * ```
 * GladlyHelpApp.init({
 *   appId: "your-own-id"
 * }).then(function() {
 *   // This is called once GladlyHelpApp has been initialized.
 *
 *   // An element with the id "start-chat" is assumed to be on the page.
 *   var startChatButton = document.getElementById('start-chat');
 *
 *   // An onClick handler can be attached to the "start-chat" element
 *   startChatButton.onclick = function() {GladlyHelpApp.show()};
 *
 *   // This API call gets the current availability
 *   var availablility = GladlyHelpApp.getAvailability();
 *
 *   // Setting the availability as the class name on the "start-chat" element,
 *   // this allows us to change the look of the button via CSS.
 *   startChatButton.setAttribute('class', availablility.toLowerCase());
 *
 *   // Subscribe to changes in availability, and update the class on the "start-chat" element.
 *   GladlyHelpApp.on('availability:change', function(availablility) {
 *     startChatButton.setAttribute('class', availablility.toLowerCase());
 *   });
 * }).catch(function(error) {
 *   // If anything goes wrong when GladlyHelpApp is being initialized, this gets called.
 *   console.log('error:', error)
 * });
 * ```
 *
 * ### Browser Support
 * The Gladly Widget supports major browsers. For the desktop, we support current + previous major
 * versions of Chrome, Firefox, and Safari along with IE11 and the current version of Microsoft Edge.
 * For mobile, we support current + previous major versions of Chrome on Android and iOS and the
 * current + previous versions of Safari on iOS.
 */

const AVAILABILITY_CHANGE_EVENT = 'availability:change';

/**
 * Map of eventName to an array of observer functions that will be called when availability change.
 * @ignore
 */
var observers = {};
observers[AVAILABILITY_CHANGE_EVENT] = [];

/**
 * Reference to Redux store.
 * @ignore
 */
var store;

/**
 * The different availability states that exist:
 * - AVAILABLE: Chat agents are ready to receive new chat messages
 * - UNAVAILABLE_OFFICE_CLOSED: Chat is unavailable due to office hours
 * - UNAVAILABLE_BUSY: Chat is unavailable due to high demand
 *
 * @memberof GladlyHelpApp
 * @name Availability
 * @constant
 * @enum {string}
 */
const AVAILABILITY = createEnum('AVAILABLE', 'UNAVAILABLE_OFFICE_CLOSED', 'UNAVAILABLE_BUSY');

/**
 * @memberof GladlyHelpApp
 * @typedef {Object} HelpAppConfig
 * @property {string} appId - id specifying your own application
 */

/**
 * @memberof GladlyHelpApp
 * @func init
 *
 * @description This method initializes the GladlyHelpApp on your site using the ID specified in the config parameter.
 * It returns a promise that will resolve when the GladlyHelpApp is ready.
 *
 * Note that `init` must be called, and the initialization must fully complete (which is signaled when the returned
 * promise is resolved) before calling any other api methods.
 *
 * @param {GladlyHelpApp.HelpAppConfig} config - Help App configuration
 * @returns {Promise}
 */
export function init(config) {
  return new Promise((resolve, reject) => {
    config = applyDefaultConfiguration(config);
    let cdn = config.cdn;
    let promise;
    if (config.demoSelfServiceEnabled && config.demoOrgId && config.demoUrl) {
      promise = configureDemo(cdn, config);
    } else {
      promise = configureApp(cdn, config);
    }
    promise
      .then(s => {
        store = s;
        startApp({ store });
        stageChangeListener(store);
        resolve(this);
      })
      .catch(error => {
        reject(error.message);
      });
  });
}

/**
 * @memberof GladlyHelpApp
 * @func getAvailability
 *
 * @description Returns chat [Availability]{@linkcode GladlyHelpApp.Availability}
 *
 * @returns {GladlyHelpApp.Availability}
 */
export function getAvailability() {
  let stage = getStage(store.getState());
  return getAppStageMapping(stage);
}

/**
 * @memberof GladlyHelpApp
 * @func on
 *
 * @description Subscribes observer to eventName and returns an unsubscribe function.
 *
 * @param {String} eventName - The name of the event to subscribe to
 * @param {Function} observer - Observer function to be invoked when event triggers
 *
 * @returns {Function} - Unsubscribe function
 */
export function on(eventName, func) {
  if (!observers[eventName]) {
    throw new Error('invalid eventName: ' + eventName);
  }
  observers[eventName].push(func);
  return () => {
    _.remove(observers[eventName], function(fn) {
      return fn === func;
    });
  };
}

/**
 * @memberof GladlyHelpApp
 *
 * @func show
 * @description Display GladlyHelpApp Chat widget
 *
 * #### Example
 * ```
 * <button onClick="GladlyHelpApp.show()">Start Chat</button>
 * ```
 */
export function show() {
  // unsupress to deactivate any rules that might prevent widget to be shown
  store.dispatch(unsuppress());
  store.dispatch(unminimizeWidget());
  store.dispatch(showWidget());
}

/**
 * When availability changes, this event emits current availability.
 *
 * @event GladlyHelpApp#availability:change
 * @param {GladlyHelpApp.Availability} - Current availability
 */

function getAppStageMapping(stage) {
  switch (stage) {
    case STAGES.CHAT_THROTTLED:
      return AVAILABILITY.UNAVAILABLE_BUSY;
    case STAGES.CHAT_CLOSED:
      return AVAILABILITY.UNAVAILABLE_OFFICE_CLOSED;
    case STAGES.CHAT:
    case STAGES.ONBOARDING:
    case STAGES.DEMO_SELF_SERVICE:
      return AVAILABILITY.AVAILABLE;
    case STAGES.SELF_SERVICE:
      return AVAILABILITY.AVAILABLE;
    default:
      return 'unknown';
  }
}

function stageChangeListener(store) {
  let currentStage = getAvailability();
  store.subscribe(() => {
    let previousStage = currentStage;
    currentStage = getAvailability();
    if (currentStage != previousStage) {
      observers[AVAILABILITY_CHANGE_EVENT].forEach(observer => observer(currentStage));
    }
  });
}
